import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Palette, FloppyDisk, CircleNotch, FileText } from '@phosphor-icons/react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPES = [
  { key: 'quotation_color', label: 'Quotations',     defaultColor: '#E07B39' },
  { key: 'invoice_color',   label: 'Invoices',       defaultColor: '#2563eb' },
  { key: 'report_color',    label: 'Reports',        defaultColor: '#1e3a5f' },
  { key: 'purchase_order_color', label: 'Purchase Documents', defaultColor: '#059669' },
];

/**
 * Document Theme Card.
 * Lets the user pick a preset palette OR override individual document accent colors.
 * Persists onto company.document_theme = { preset, <doc>_color, ... }
 */
export default function DocumentThemeCard({ company, canEdit, onSaved }) {
  const [presets, setPresets] = useState(null);
  const [theme, setTheme] = useState({ preset: 'default' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTheme(company?.document_theme || { preset: 'default' });
  }, [company?.id, company?.document_theme]);

  useEffect(() => {
    axios.get(`${API_URL}/company/document-theme/presets`)
      .then((r) => setPresets(r.data?.presets || {}))
      .catch(() => setPresets({}));
  }, []);

  const updatePreset = (preset) => {
    // Selecting a preset clears explicit per-doc overrides
    setTheme({ preset });
  };

  const updateColor = (key, value) => {
    setTheme((prev) => ({ ...(prev || {}), preset: 'custom', [key]: value }));
  };

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const res = await axios.patch(`${API_URL}/company`, { document_theme: theme });
      toast.success('Document theme saved. New PDFs will use these colors.');
      onSaved?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save theme');
    } finally {
      setSaving(false);
    }
  };

  const presetEntries = presets ? Object.entries(presets) : [];
  const activePreset = theme?.preset || 'default';

  return (
    <div className="bg-card border rounded-2xl p-6 space-y-6" data-testid="document-theme-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Palette size={20} weight="duotone" />
          </div>
          <div>
            <h3 className="font-heading font-semibold">Document Theme</h3>
            <p className="text-xs text-muted-foreground">Choose colors for your invoices, quotations & reports.</p>
          </div>
        </div>
      </div>

      {/* Presets */}
      <div className="space-y-3">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Palette presets</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {presetEntries.map(([name, colors]) => {
            const active = activePreset === name;
            return (
              <button
                key={name}
                type="button"
                disabled={!canEdit}
                onClick={() => updatePreset(name)}
                data-testid={`preset-${name}`}
                className={`group rounded-xl border p-3 text-left transition-all ${
                  active ? 'border-primary ring-2 ring-primary/30 shadow-sm' : 'border-border hover:border-primary/40'
                } ${!canEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <div className="flex gap-1.5 mb-2">
                  {['quotation', 'invoice', 'report', 'purchase'].map((k) => (
                    <span
                      key={k}
                      className="w-6 h-6 rounded-md ring-1 ring-black/5"
                      style={{ background: colors[k] }}
                    />
                  ))}
                </div>
                <div className="text-xs font-medium capitalize">{name}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Per-doc overrides */}
      <div className="space-y-3">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">
          Per-document accent (advanced)
        </Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {DOC_TYPES.map((d) => {
            const value = theme?.[d.key] || d.defaultColor;
            return (
              <div key={d.key} className="flex items-center gap-3 border rounded-lg p-3">
                <FileText size={20} weight="duotone" className="text-muted-foreground" />
                <div className="flex-1">
                  <div className="text-sm font-medium">{d.label}</div>
                  <div className="text-[11px] text-muted-foreground font-mono">{value}</div>
                </div>
                <input
                  type="color"
                  disabled={!canEdit}
                  value={value}
                  onChange={(e) => updateColor(d.key, e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer disabled:cursor-not-allowed bg-transparent"
                  data-testid={`color-${d.key}`}
                />
                <Input
                  disabled={!canEdit}
                  value={value}
                  onChange={(e) => updateColor(d.key, e.target.value)}
                  className="w-28 font-mono text-xs"
                  data-testid={`color-input-${d.key}`}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <Button
          type="button"
          onClick={handleSave}
          disabled={!canEdit || saving}
          data-testid="save-document-theme"
        >
          {saving ? <CircleNotch className="mr-2 animate-spin" size={16} /> : <FloppyDisk className="mr-2" size={16} />}
          Save Document Theme
        </Button>
      </div>
    </div>
  );
}
